import TextField from '@mui/material/TextField';
import {
  OrganisationContext,
  OrganisationResponse,
  TeamResponse,
  TeamVisibility,
} from 'api';
import {
  Dialog,
  TextV2,
  RadioGroupControl,
  SvgLock2Bold,
  SvgMultipleUsers2Bold,
  SectionTitle,
  SvgBuilding2Bold,
} from '@insights-ltd/design-library/components';
import { ReactNode, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import { TeamFormData } from 'pages/CreateTeam/types';
import { User } from 'types/types';
import BulkUploadTeam from 'components/BulkUploadLearners/BulkUploadLearners';
import { useGetAllOrganisationsAndGroups } from 'components/hooks/useGetAllOrganisationsAndGroups';
import { Box, styled } from '@mui/material';
import { useGetViewPermissionOptions } from 'pages/CreateTeam/CreateTeam.helper';
import { OrgsListSearch } from 'components/OrgsSearchList/OrgsSearchList';
import { GroupOrganisationHashMap } from 'utils/createGroupOrganisationMap';
import { TeamData } from './types';
import OrganisationSection from './OrganisationSection';
import hasTeamDetailsChanged from './hasTeamDetailsChanged';

const StyledP = styled(TextV2)({
  fontWeight: '600 !important',
  lineHeight: '28px !important',
  marginTop: '0 !important',
  marginBottom: '8px !important',
});

const RemoveDialectOrgContent = () => {
  return (
    <TextV2 color="textSecondary">
      <Trans
        i18nKey="ui.event-management.teams.edit.remove-org.content"
        components={{
          bold: (
            <TextV2 component="span" color="textSecondary" variant="bodyBold" />
          ),
        }}
      />
    </TextV2>
  );
};

type EditTeamFormProps = {
  team: TeamResponse;
  onSubmit: (formData: TeamData) => Promise<void>;
  children: (formState: {
    hasChanges: boolean;
    isSubmitting: boolean;
  }) => ReactNode;
};

const sortOrgs = (
  { name: nameA }: OrganisationResponse,
  { name: nameB }: OrganisationResponse,
) => (nameA > nameB ? 1 : -1);

const StyledForm = styled('form')(({ theme }) => ({
  '> * + *': {
    marginTop: '40px',
  },
  '> * > *': {
    marginTop: theme.spacing(spacingSizeMap.M),
  },
  '> div > fieldset': {
    marginTop: '8px !important',
  },
}));

const EditTeamForm = ({ team, onSubmit, children }: EditTeamFormProps) => {
  const { t } = useTranslation();
  const isMultiOrg = team.organisationContext?.type === 'MULTIPLE';
  const { organisationsInGroups } = useGetAllOrganisationsAndGroups({
    enabled: isMultiOrg,
  });

  const [openRemoveOrgDialog, setOpenRemoveOrgDialog] = useState<{
    open: boolean;
    orgId?: string;
  }>({ open: false, orgId: undefined });

  const {
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
    setValue,
    trigger,
    watch,
  } = useForm<TeamFormData>({
    defaultValues: {
      ...team,
      selectedOrgs: [...(team.organisationContext?.organisations ?? [])].sort(
        sortOrgs,
      ),
    },
  });

  const selectedOrgs = watch('selectedOrgs') ?? [];
  const name = watch('name');
  const visibility = watch('visibility');

  const [uploadedLearners, setUploadedLearners] = useState<User[]>([]);

  const hasChanges =
    uploadedLearners.length > 0 ||
    hasTeamDetailsChanged(team, { name, visibility, selectedOrgs });
  const [selectedVisibility, setSelectedVisibility] = useState<TeamVisibility>(
    team.visibility || 'PRIVATE',
  );

  const visibilityOptions = useGetViewPermissionOptions(isMultiOrg);

  const teamOrgId = team.organisationContext?.organisations[0].id ?? '';

  register('visibility', { required: true });

  const messages = {
    required: t('ui.event-management.teams.edit.error.name-required'),
    maxLength: t('ui.event-management.teams.edit.error.max-length', {
      maxLength: 100,
    }),
  };

  const submitHandler = handleSubmit((form: TeamData) =>
    onSubmit({ ...form, learners: uploadedLearners }),
  );
  const onBulkAddLearners = (data: User[]) => {
    const deDupedLearners = data.filter(
      (row) => !uploadedLearners.find((item) => item.id === row.id),
    );
    setUploadedLearners([...uploadedLearners, ...deDupedLearners]);
  };

  const errorText =
    errors.name && messages[errors.name.type as keyof typeof messages];

  const handleVisibilityChange = (value: string) => {
    setSelectedVisibility(value as TeamVisibility);
    setValue('visibility', value as TeamVisibility);
    trigger('visibility').then();
  };

  const handleOnRemove = (orgId: string) => {
    setOpenRemoveOrgDialog({ open: true, orgId });
  };

  const handleConfirmRemove = (orgId: string) => {
    setValue(
      'selectedOrgs',
      selectedOrgs.filter(({ id }) => id !== orgId),
    );
  };

  const getOrgGroupHashMap = (
    organisationContext?: OrganisationContext,
  ): GroupOrganisationHashMap => {
    const org = organisationContext?.organisations[0];
    if (org?.group === undefined) return {};
    return {
      [org.id]: {
        ...org.group,
      },
    };
  };

  return (
    <StyledForm noValidate autoComplete="off" onSubmit={submitHandler}>
      {/* Organisation */}
      <div>
        <Box
          sx={{
            my: spacingSizeMap.L,
          }}
        >
          <SectionTitle
            icon={{ icon: <SvgBuilding2Bold />, iconColour: 'orange' }}
            text={t('ui.event-management.users.create.organisation', {
              count: team.organisationContext?.organisations?.length ?? 0,
            })}
          />
        </Box>
        {!isMultiOrg ? (
          <OrganisationSection
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            items={team.organisationContext!.organisations}
            organisationsInGroups={getOrgGroupHashMap(team.organisationContext)}
          />
        ) : (
          <OrgsListSearch
            onSelect={(value: OrganisationResponse) =>
              setValue('selectedOrgs', [...selectedOrgs, value].sort(sortOrgs))
            }
            onRemoveOrg={selectedOrgs.length > 2 ? handleOnRemove : undefined}
            selectedOrgs={selectedOrgs}
            dataTestId="team-org-picker"
            groupHashMap={organisationsInGroups}
          />
        )}
      </div>

      {/* Team name */}
      <div>
        <Box
          sx={{
            my: spacingSizeMap.L,
          }}
        >
          <SectionTitle
            icon={{ icon: <SvgMultipleUsers2Bold />, iconColour: 'blue' }}
            text={t('ui.event-management.teams.edit-team-info')}
          />
        </Box>
        <TextField
          id="name"
          label={t('ui.event-management.teams.team-name')}
          placeholder={t('ui.event-management.teams.add.placeholder.team-name')}
          variant="outlined"
          fullWidth
          sx={{ marginTop: '0 !important' }}
          type="text"
          {...register('name', { required: true, maxLength: 100 })}
          error={Boolean(errors.name)}
          helperText={errorText}
        />
      </div>

      {/* Learners */}
      {!team.migrated && team.organisationContext?.type !== 'MULTIPLE' && (
        <div>
          <BulkUploadTeam
            organisationId={teamOrgId}
            onBulkAddLearners={onBulkAddLearners}
          />
        </div>
      )}

      {/* Visibility permissions */}
      <div>
        <Box
          sx={{
            my: spacingSizeMap.L,
          }}
        >
          <SectionTitle
            icon={{ icon: <SvgLock2Bold />, iconColour: 'pink' }}
            text={t('ui.event-management.teams.view-permissions')}
          />
        </Box>
        <StyledP>
          {t('ui.event-management.teams.view-permissions.view-help-text')}
        </StyledP>
        <RadioGroupControl
          options={visibilityOptions}
          name="visibility"
          title={t('ui.event-management.users.create.permission-roles')}
          value={selectedVisibility}
          onChange={handleVisibilityChange}
          variant="boxed"
        />
      </div>

      {children({ hasChanges, isSubmitting })}

      <Dialog
        open={openRemoveOrgDialog.open}
        title={t('ui.event-management.teams.edit.remove-org.title')}
        primaryButtonText={t(
          'ui.event-management.teams.edit.remove-org.confirm',
        )}
        secondaryButtonText={t(
          'ui.event-management.teams.edit.remove-org.cancel',
        )}
        dialogProps={{
          sx: {
            '> div > div > div': {
              minHeight: '0',
            },
          },
        }}
        content={<RemoveDialectOrgContent />}
        onSubmit={() => {
          handleConfirmRemove(openRemoveOrgDialog.orgId ?? '');
          setOpenRemoveOrgDialog({ open: false, orgId: undefined });
        }}
        onClose={() => {
          setOpenRemoveOrgDialog({ open: false, orgId: undefined });
        }}
      />
    </StyledForm>
  );
};

export default EditTeamForm;
